/**
 * Local UI state of the scribe lifecycle. The middleware doesn't care about
 * `paused` or `finalizing` — they're purely client-side hints. `finalizing`
 * means the doctor has clicked End Session and the orchestration is running
 * (POST /finalize → POST /generate-draft); `useLiveTranscription` detaches
 * its Zoom LTT subscription when it sees this state so no new captions are
 * captured while the AI is generating the Progress Note.
 */
import type { ScribeSourceLanguage } from '@behuman/core-contract'

export type ScribeState = 'idle' | 'streaming' | 'paused' | 'finalizing'

/**
 * Live presence of the PATIENT (the remote participant) in the Zoom call, as
 * seen from the provider's client. `not-joined` until a remote participant is
 * first detected; `connected` while one is present; `disconnected` after one
 * was present and then left (distinct from never-joined so the UI can say
 * "reconnecting…" vs "waiting to join"). Roster-derived, so it self-corrects.
 */
export type PatientPresence = 'not-joined' | 'connected' | 'disconnected'

/**
 * A recoverable scribe session discovered on load (interrupted-visit recovery).
 * Carries enough to render the resume card and to drive resume/finish/discard.
 */
export interface InterruptedSession {
  encounterId: string
  patientId: string
  sessionId: string
  chunkCount: number
  startedAt: string
  lastChunkAt?: string
}

export interface CallSessionState {
  /**
   * Coarse call status. `idle` = nothing live; `interrupted` = a recoverable
   * session was found on load (not yet joined); `active` = a live Zoom call is
   * joined. Distinct from `isJoined` so the chart resume card can render
   * without a live call.
   */
  status: 'idle' | 'interrupted' | 'active'
  /** Set with the recovered session while `status === 'interrupted'`; else null. */
  interruptedSession: InterruptedSession | null
  currentEncounterId: string | null
  isJoined: boolean
  /**
   * Opaque server-issued scribe session id (UUID) returned by
   * `POST /scribe/sessions/:encounterId/start`. Held in provider state so
   * downstream consumers (`useLiveTranscription`, `useFinalizeScribeSession`,
   * the recording badge, etc.) all read from a single source of truth.
   * Null until the doctor clicks "Start Scribe" in `<ScribeControls>`.
   */
  scribeSessionId: string | null
  /**
   * Local UI state for the scribe controls + recording badge. Lifted to the
   * provider so `<ScribeControls>` and the inline `<Indicator>` in
   * `<ZoomPanel>` read the same value without an extra context.
   */
  scribeState: ScribeState
  /** Live presence of the remote patient participant (roster-derived). */
  patientPresence: PatientPresence
  /** Live presence of a non-patient staff participant (care coordinator), roster-derived. */
  staffPresence: PatientPresence
  /** Staff participant's display name; the roster scan omits it while they're absent, so keep the last seen. */
  staffDisplayName: string | null
  /**
   * PD-1600 — the language the visit is spoken in. Picked before the join (or
   * adopted from a live session on resume); pins Zoom live transcription and
   * rides to the server on `start`. Survives JOIN, resets with the provider.
   */
  encounterLanguage: ScribeSourceLanguage
}

export type CallSessionAction =
  | {
      type: 'JOIN'
      encounterId: string
      /**
       * Interrupted-visit recovery: when present, the doctor chose "Resume
       * call" in the recovery modal and we ADOPT this server-issued scribe
       * session id instead of starting a fresh one. The reducer seeds
       * `scribeSessionId` with it and flips `scribeState` straight to
       * 'streaming' so `<ZoomPanel>`'s auto-start effect (gated on
       * `scribeState === 'idle'`) skips and the live transcription continues
       * against the SAME session. Absent on a normal fresh join, where the
       * scribe id is hard-reset to null (see the JOIN case below).
       */
      resumeSessionId?: string
    }
  | { type: 'SET_SCRIBE_SESSION'; sessionId: string | null }
  | { type: 'SET_SCRIBE_STATE'; scribeState: ScribeState }
  | { type: 'SET_ENCOUNTER_LANGUAGE'; language: ScribeSourceLanguage }
  // Raw roster signal for both remote roles; the reducer derives each label so a
  // transient absence after a connect becomes 'disconnected', not 'not-joined'.
  | {
      type: 'SET_ROSTER_PRESENCE'
      patientPresent: boolean
      staffPresent: boolean
      staffDisplayName?: string
    }
  | { type: 'HYDRATE_INTERRUPTED'; session: InterruptedSession }
  | { type: 'CLEAR_INTERRUPTED' }
  | { type: 'RESET' }

export const initialCallSessionState: CallSessionState = {
  status: 'idle',
  interruptedSession: null,
  currentEncounterId: null,
  isJoined: false,
  scribeSessionId: null,
  scribeState: 'idle',
  patientPresence: 'not-joined',
  staffPresence: 'not-joined',
  staffDisplayName: null,
  encounterLanguage: 'en',
}

// Present → connected. Absent → 'disconnected' if it was ever connected, else
// stays 'not-joined'.
function derivePresence(present: boolean, prior: PatientPresence): PatientPresence {
  return present ? 'connected' : prior === 'not-joined' ? 'not-joined' : 'disconnected'
}

export function callSessionReducer(
  state: CallSessionState,
  action: CallSessionAction,
): CallSessionState {
  switch (action.type) {
    case 'JOIN':
      // Interrupted-visit recovery — ADOPT the recovered scribe session id and
      // resume streaming into it. We do NOT mint a new session: the doctor is
      // rejoining the same live encounter, so the prior buffer stays the
      // single source of truth. `<ZoomPanel>`'s auto-start is gated on
      // `scribeState === 'idle'`, so seeding 'streaming' here makes it skip.
      if (action.resumeSessionId) {
        return {
          status: 'active',
          interruptedSession: null,
          currentEncounterId: action.encounterId,
          isJoined: true,
          scribeSessionId: action.resumeSessionId,
          scribeState: 'streaming',
          patientPresence: 'not-joined',
          staffPresence: 'not-joined',
          staffDisplayName: null,
          encounterLanguage: state.encounterLanguage,
        }
      }
      // Joining a fresh session always clears any stale scribe id + state —
      // the prior id is server-bound to the prior encounter and would be
      // rejected if reused.
      return {
        status: 'active',
        interruptedSession: null,
        currentEncounterId: action.encounterId,
        isJoined: true,
        scribeSessionId: null,
        scribeState: 'idle',
        patientPresence: 'not-joined',
        staffPresence: 'not-joined',
        staffDisplayName: null,
        encounterLanguage: state.encounterLanguage,
      }
    case 'SET_SCRIBE_SESSION':
      return { ...state, scribeSessionId: action.sessionId }
    case 'SET_SCRIBE_STATE':
      return { ...state, scribeState: action.scribeState }
    case 'SET_ENCOUNTER_LANGUAGE':
      return state.encounterLanguage === action.language
        ? state
        : { ...state, encounterLanguage: action.language }
    case 'SET_ROSTER_PRESENCE': {
      // Derive each label from its raw roster bit; no-op when nothing changed so
      // the memoized context value stays referentially stable across the
      // roster's frequent re-scans.
      const patientPresence = derivePresence(action.patientPresent, state.patientPresence)
      const staffPresence = derivePresence(action.staffPresent, state.staffPresence)
      const staffDisplayName = action.staffDisplayName ?? state.staffDisplayName
      if (
        patientPresence === state.patientPresence &&
        staffPresence === state.staffPresence &&
        staffDisplayName === state.staffDisplayName
      ) {
        return state
      }
      return { ...state, patientPresence, staffPresence, staffDisplayName }
    }
    case 'HYDRATE_INTERRUPTED':
      // Never override a live call — a benign race where hydration resolves
      // after a join must not clobber the active session.
      if (state.isJoined || state.status === 'active') return state
      return { ...state, status: 'interrupted', interruptedSession: action.session }
    case 'CLEAR_INTERRUPTED':
      return { ...state, status: 'idle', interruptedSession: null }
    case 'RESET':
      return initialCallSessionState
    default:
      return state
  }
}
